/*
 * AdminShippingAccounts — Verqo Ops: Verqo Shipping child user management.
 *
 * Displays every EP child user under the Verqo parent account, joined
 * with the local Verqo tenant it's provisioned for. Actions:
 *   · Create child for a tenant (idempotent)
 *   · Retrieve details + API keys hint
 *   · Delete child user (soft-deletes local record too)
 *   · Reconcile (bulk sync EP ↔ local mirror)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Package, Trash2, RefreshCcw, Plus, Copy, ExternalLink, Shield, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";


export default function AdminShippingAccounts() {
  const [loading, setLoading]       = useState(false);
  const [children, setChildren]     = useState([]);
  const [health, setHealth]         = useState(null);
  const [tenants, setTenants]       = useState([]);
  const [creatingFor, setCreatingFor] = useState(null); // tenant id
  const [newlyCreated, setNewlyCreated] = useState(null); // dict with api keys to show ONCE
  const [reconciling, setReconciling] = useState(false);
  const [q, setQ]                   = useState("");

  // Iter 239 · Luma AI ruleset config
  const [luma, setLuma]             = useState({ ruleset_name: "", validated: false, updated_at: null });
  const [lumaInput, setLumaInput]   = useState("");
  const [lumaSaving, setLumaSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ch, hh, tt, lc] = await Promise.all([
        api.get("/admin/easypost/children?page_size=100").then(r => r.data).catch(() => ({ children: [] })),
        api.get("/easypost/health").then(r => r.data).catch(() => null),
        api.get("/admin/easypost/tenants?page_size=200").then(r => r.data).catch(() => []),
        api.get("/admin/easypost/luma-config").then(r => r.data).catch(() => null),
      ]);
      setChildren(ch.children || []);
      setHealth(hh);
      setTenants(Array.isArray(tt) ? tt : (tt.tenants || []));
      if (lc) {
        setLuma(lc);
        setLumaInput(lc.ruleset_name || "");
      }
    } catch (e) {
      toast.error("Failed to load Verqo Shipping sub-accountren");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredTenants = useMemo(() => {
    const s = q.trim().toLowerCase();
    const existingIds = new Set(children.map(c => c.tenant_id).filter(Boolean));
    return tenants
      .filter(t => !existingIds.has(t.id))
      .filter(t => !s || (t.name || "").toLowerCase().includes(s) || (t.id || "").includes(s))
      .slice(0, 50);
  }, [tenants, children, q]);

  const createFor = async (tenant) => {
    setCreatingFor(tenant.id);
    try {
      const { data } = await api.post("/admin/easypost/children", {
        tenant_id: tenant.id,
        name:      tenant.name || `verqo-${tenant.id.slice(0, 8)}`,
      });
      if (data.already_exists) {
        toast.info(`Tenant already has EP child ${data.easypost_user_id}`);
      } else {
        toast.success(`Provisioned Verqo Shipping sub-account user for ${tenant.name}`);
        setNewlyCreated(data.child); // show api keys once
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create child user");
    } finally {
      setCreatingFor(null);
    }
  };

  const deleteChild = async (child) => {
    if (!window.confirm(
      `Delete Verqo Shipping sub-account ${child.name || child.id}?\n\n` +
      `This is IRREVERSIBLE — the child loses access to all shipment history. ` +
      `Local audit record is preserved.`
    )) return;
    try {
      await api.delete(`/admin/easypost/children/${child.id}`);
      toast.success("Child user deleted from Verqo Shipping");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/easypost/reconcile");
      toast.success(
        `Reconciled: ${data.total_in_ep} in Verqo Shipping · ${data.orphans} orphans · ${data.stale} stale`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  const saveLuma = async () => {
    const name = lumaInput.trim();
    if (!name) return toast.error("Ruleset name is required");
    setLumaSaving(true);
    try {
      const { data } = await api.put("/admin/easypost/luma-config", { ruleset_name: name });
      if (data.validated) {
        toast.success(`Verqo Smart Rate ruleset "${name}" saved and validated`);
      } else {
        toast.warning(`Saved, but validation failed: ${data.warning || "ruleset not found in EP dashboard"}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save ruleset");
    } finally {
      setLumaSaving(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="admin-easypost-children-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Package className="w-6 h-6" /> Verqo Shipping · Sub-Accounts
          </h1>
          <p className="text-sm text-neutral-600 mt-1 max-w-3xl">
            Verqo runs a <strong>centralized-billing Forge model</strong>: every seller-tenant has a
            child user provisioned under Verqo&apos;s parent Verqo Shipping account. Verqo Shipping bills Verqo once;
            Verqo bills each seller via their postage wallet. Delete a child to revoke shipping access.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={reconcile}
            disabled={reconciling}
            data-testid="ep-reconcile-btn"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${reconciling ? "animate-spin" : ""}`} />
            Reconcile
          </Button>
        </div>
      </div>

      {/* Health banner */}
      {health && (
        <Card className="p-4 flex items-center gap-4 flex-wrap" data-testid="ep-health-card">
          <Badge variant={health.configured ? "default" : "destructive"}>
            {health.configured ? "🟢 Configured" : "🔴 Not configured"}
          </Badge>
          <Badge variant="secondary">SDK · {health.sdk_available ? "loaded" : "missing"}</Badge>
          <Badge variant="secondary">Webhook · {health.webhook_secret_set ? "secured" : "no secret"}</Badge>
          <span className="text-sm text-neutral-700">
            <strong>{health.carrier_count || 0}</strong> carriers on parent account:{" "}
            {(health.carriers || []).join(", ")}
          </span>
          <a
            className="text-xs underline text-neutral-500 hover:text-neutral-700 flex items-center gap-1 ml-auto"
            href="/admin/verqo-shipping-accounts"
            target="_blank" rel="noreferrer"
          >
            Verqo Shipping docs <ExternalLink className="w-3 h-3" />
          </a>
        </Card>
      )}

      {/* Iter 239 · Luma AI Smart Rate ruleset config */}
      <Card className="p-4" data-testid="luma-ruleset-card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-500" />
              Verqo Smart Rate · AI Ruleset
              {luma.validated ? (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> validated
                </Badge>
              ) : luma.ruleset_name ? (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                  <AlertTriangle className="w-3 h-3 mr-1" /> saved · not validated
                </Badge>
              ) : (
                <Badge variant="secondary">not configured</Badge>
              )}
            </h2>
            <p className="text-sm text-neutral-600 mt-1 max-w-3xl">
              Verqo Smart Rate uses an AI ruleset (configured on the parent shipping account) to
              auto-pick the best carrier + service per shipment. Rulesets are defined once, then
              used platform-wide by every seller when they book labels.
              {" "}
              <a
                className="underline hover:text-neutral-800"
                href="https://support.easypost.com/hc/en-us/articles/25128262167195"
                target="_blank" rel="noreferrer"
              >
                How to create a ruleset <ExternalLink className="inline w-3 h-3" />
              </a>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <label className="text-xs text-neutral-500 uppercase tracking-wide">
              Default Ruleset Name (as configured in the shipping provider dashboard)
            </label>
            <Input
              value={lumaInput}
              onChange={e => setLumaInput(e.target.value)}
              placeholder="e.g. cheapest-3day, fastest-under-10, verqo-default"
              className="mt-1"
              data-testid="luma-ruleset-input"
            />
          </div>
          <div className="pt-5">
            <Button
              onClick={saveLuma}
              disabled={lumaSaving || !lumaInput.trim()}
              data-testid="luma-ruleset-save"
            >
              {lumaSaving ? "Validating…" : "Save & Validate"}
            </Button>
          </div>
        </div>
        {luma.updated_at && (
          <div className="text-xs text-neutral-500 mt-3">
            Last updated: {new Date(luma.updated_at).toLocaleString()}
          </div>
        )}
      </Card>

      {/* Existing children table */}
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="font-medium text-sm text-neutral-800">
            Existing children · {children.length}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase">
              <tr>
                <th className="px-4 py-2 text-left">Tenant</th>
                <th className="px-4 py-2 text-left">EP Child ID</th>
                <th className="px-4 py-2 text-left">Verified</th>
                <th className="px-4 py-2 text-left">Keys stored</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Created</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-6 text-center text-neutral-500">Loading…</td></tr>
              )}
              {!loading && children.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-neutral-500">
                    No child users yet. Provision one from the panel below.
                  </td>
                </tr>
              )}
              {children.map(c => (
                <tr key={c.id} className="border-t hover:bg-neutral-50" data-testid={`ep-child-row-${c.id}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{c.tenant_name || c.name || "—"}</div>
                    <div className="text-xs text-neutral-500 truncate max-w-xs">
                      {c.tenant_id || "not linked to a Verqo tenant"}
                    </div>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{c.id}</td>
                  <td className="px-4 py-2">
                    {c.verified
                      ? <Badge variant="default">✓ verified</Badge>
                      : <Badge variant="secondary">pending</Badge>}
                  </td>
                  <td className="px-4 py-2">
                    {c.has_prod_key && <Badge variant="secondary" className="mr-1">prod</Badge>}
                    {c.has_test_key && <Badge variant="secondary">test</Badge>}
                    {!c.has_prod_key && !c.has_test_key && <span className="text-xs text-neutral-400">none</span>}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={c.local_status === "active" ? "default" : "secondary"}>
                      {c.local_status || "unknown"}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-xs text-neutral-500">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => deleteChild(c)}
                      data-testid={`ep-child-delete-${c.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create panel */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-medium text-sm text-neutral-800">
            Provision a child user for a tenant
          </h2>
          <Input
            placeholder="Search tenants by name or id…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="max-w-xs"
            data-testid="ep-tenant-search"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredTenants.length === 0 && (
            <div className="col-span-full text-sm text-neutral-500 py-4">
              All tenants already have child users, or no matches found.
            </div>
          )}
          {filteredTenants.map(t => (
            <div
              key={t.id}
              className="border rounded-lg p-3 hover:bg-neutral-50 flex items-center justify-between"
              data-testid={`ep-tenant-row-${t.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{t.name || "(unnamed tenant)"}</div>
                <div className="text-xs text-neutral-500 truncate">{t.id}</div>
              </div>
              <Button
                size="sm"
                onClick={() => createFor(t)}
                disabled={creatingFor === t.id}
                data-testid={`ep-tenant-provision-${t.id}`}
              >
                <Plus className="w-4 h-4 mr-1" />
                {creatingFor === t.id ? "..." : "Provision"}
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* One-time API key display modal */}
      <Dialog open={!!newlyCreated} onOpenChange={(open) => !open && setNewlyCreated(null)}>
        <DialogContent className="max-w-2xl" data-testid="ep-newly-created-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-500" />
              Child user created · save your production API key
            </DialogTitle>
          </DialogHeader>
          {newlyCreated && (
            <div className="space-y-4 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">
                <strong>Save the production key below.</strong> Verqo Shipping does not allow retrieval later.
                Verqo has stored it internally, but if you ever need to copy it for direct API access,
                do it now.
              </div>
              <div>
                <div className="text-xs text-neutral-500 mb-1">Child user ID</div>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-neutral-100 px-2 py-1 rounded flex-1 truncate">
                    {newlyCreated.id}
                  </code>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(newlyCreated.id, "Child ID")}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {(newlyCreated.api_keys || []).map(k => (
                <div key={k.id}>
                  <div className="text-xs text-neutral-500 mb-1 flex items-center gap-2">
                    {k.mode.toUpperCase()} API key
                    <Badge variant="secondary">{k.mode}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-neutral-100 px-2 py-1 rounded flex-1 font-mono break-all">
                      {k.key}
                    </code>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => copyToClipboard(k.key, `${k.mode} key`)}
                      data-testid={`ep-copy-key-${k.mode}`}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                onClick={() => setNewlyCreated(null)}
                data-testid="ep-newly-created-close"
              >
                Done · I&apos;ve saved the key
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
